/**
 * @description 移动端通用的方法
 * @author aprilcai
 */
import { resultCode, resultMessage, wrapResult } from '../module/entity';
import { onceAsync, invokeAsync, once } from '../module/bridge';
import { $getPayUrl, $commonGetUserInfo } from './pc-web-common-method';
import { isIPhone } from '../module/ua';
import cookies from '../../../../shared/src/cookies';
import { updateTenvideoAppSession } from '../module/util';

// 登录
export async function mobileLogin(): Promise<IResult> {
  const res = await invokeAsync('actionLogin', { type: 'tv' });
  const { errCode, errMsg } = res;

  // 检测到已登录但是没有cookie中没有main_login
  // 调用updateTenvideoAppSession方法，从js api中获取cookie再写入当前页面
  // 等待同步完app的cookie之后再回调成功
  const hasMainLogin = Boolean(cookies.getItem('main_login'));
  if (errCode === 0 && !hasMainLogin) {
    console.log('检测到登录成功后cookie中没有main_login, 调用updateTenvideoAppSession同步一下');
    await updateTenvideoAppSession();
  }
  return wrapResult(errCode, errMsg);
}

// 本地存储
export async function mobileAccessLocal(param: {
  type: string;
  key: string;
  value: string;
}): Promise<IResult> {
  const { type, key, value } = param;
  let res;
  if (type === 'get') {
    const k = [key];
    res = await invokeAsync('getNativeStorage', {
      key: k,
    });
    const { result } = res;
    res.result = {
      textValue: result,
    };
  } else {
    const p: { [key: string]: string } = {};
    p[key] = value;
    res = await invokeAsync('setNativeStorage', p);
  }
  return wrapResult(res.errCode, res.errMsg, res.result);
}

// 关闭
export async function mobileClose(): Promise<IResult> {
  setTimeout(() => {
    invokeAsync('close', null);
  }, 100);
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 是否登录
async function isLogin(): Promise<IResult> {
  const res = await invokeAsync('getMainLoginType', null);
  const { errCode, errMsg, result } = res;
  return wrapResult(errCode, errMsg, {
    isLogin: result === 'qq' || result === 'wx',
  });
}

// 获取用户信息
export async function mobileGetUserInfo(param: { data: any; isTest: boolean }): Promise<IResult> {
  const isLoginResult = (await isLogin()).result.isLogin;
  if (!isLoginResult) {
    return wrapResult(resultCode.error, 'no login', {
      appid: '',
      openid: '',
      token: '',
      type: '',
      baseInfo: {
        avatar: '',
        nickname: '',
        vip: false,
      },
    });
  }
  const { result } = await invokeAsync('getMainUserInfo', null);
  const userInfo = await $commonGetUserInfo(param);
  const { type } = result;
  const userInfoResult = userInfo.result;
  if (type === 'wx' || type === 'qq') {
    userInfoResult.baseInfo.avatar = result.userInfo.headImgUrl;
    userInfoResult.type = type;
    return wrapResult(resultCode.ok, resultMessage.ok, userInfo);
  }
  return wrapResult(resultCode.error, 'no login ');
}

// 获取用户设备id
export async function mobileGetDeviceId(): Promise<IResult> {
  const res = await invokeAsync('getDeviceInfo', null);
  const { errCode, errMsg, result } = res;
  return wrapResult(errCode, errMsg, {
    id: result.guid,
  });
}

// 获取网络状态
export async function mobileGetNetworkState(): Promise<IResult> {
  const res = await invokeAsync('getNetworkState', null);
  const { result } = res;
  return wrapResult(resultCode.ok, resultMessage.ok, result);
}

// 打开webview
export async function mobileOpenWebview(param: {
  url: string;
  close?: number;
  style?: number;
}): Promise<IResult> {
  const { url } = param;
  // ios下跳转有bug，如果直接用openUrl的方式，会app退到后台后被回收的bug，所以这里用openView的方式来打开
  const newUrl = isIPhone ? url : `tenvideo2://?action=65&openurl=${encodeURIComponent(url)}`;
  const method = isIPhone ? 'openUrl' : 'openView';
  invokeAsync(method, {
    ...param,
    ...{
      url: newUrl,
    },
  });
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 打开支付页
export async function mobileOpenPayPage(param: {
  appid?: string;
  actid?: string;
  orderid?: string;
  needpay?: string;
  close?: number;
  ru?: string;
  title?: string;
  sandbox?: number;
  autopay?: number;
}): Promise<IResult> {
  const { result } = await $getPayUrl(param);
  await invokeAsync('openUrl', {
    url: result.payUrl,
  });
  return wrapResult(resultCode.ok, resultMessage.ok);
}

async function toolsDialogAction() {
  return new Promise((resolve) => {
    once('onShareIconClick', () => {
      resolve('onShareIconClick');
    });
    once('onToolsDialogClose', () => {
      resolve('onToolsDialogClose');
    });
  });
}

// 分享
export async function mobileShare(param: {
  title: string;
  subTitle?: string;
  url: string;
  imageUrl: string;
  sharePageTitle?: string;
  sharePageLaunchUrl?: string;
}): Promise<IResult> {
  const { sharePageLaunchUrl, sharePageTitle } = param;
  const { url } = param;
  let shareUrl = '';
  if (sharePageLaunchUrl) {
    const origin = /^https/.test(sharePageLaunchUrl) ? 'https://m.v.qq.com' : 'http://m.v.qq.com';
    const title = sharePageTitle ? `&title=${encodeURIComponent(sharePageTitle)}` : '';
    shareUrl = `${origin}/txi/tools/launch_app.html?open_url=${encodeURIComponent(sharePageLaunchUrl)}${title}`;
  }
  const res = await invokeAsync('setMoreInfo', {
    hasShare: true,
    shareInfo: { ...param, ...{ url: shareUrl || url } },
  });
  const { errCode } = res;
  if (errCode === 0) {
    await invokeAsync('openToolsDialog', null);

    const toolsAction = await toolsDialogAction();
    if (toolsAction === 'onShareIconClick') {
      // 分享结束的回调，根据结果判断是否分享成功
      const shareRes = await onceAsync('onShareFinish');
      if (shareRes.errCode === 0) {
        return wrapResult(resultCode.ok, resultMessage.ok);
      }
      return wrapResult(
        resultCode.error,
        `${resultMessage.error}, onShareFinish(${shareRes.errCode})`,
      );
    }
    return wrapResult(resultCode.error, `${resultMessage.error}, user did not click share icon`);
  }
  return wrapResult(resultCode.error, `${resultMessage.error}, setMoreInfo(${errCode})`);
}

// 写日志
export async function mobileWriteLog(param: {
  appid: string;
  content: string;
  isReportServer: boolean;
  isConsoleLog: boolean;
}): Promise<IResult> {
  const { appid, content } = param;
  const logContent = `bridge-helper: ${new Date().toLocaleString()}, appid=${appid}, content=${content}`;
  await invokeAsync('writeLog', {
    content: logContent,
  });
  return wrapResult(resultCode.ok, resultMessage.ok);
}

// 打开VIP开通页/浮层
export async function mobileOpenPayVipView(param: { url?: string }) {
  return mobileOpenWebview({
    url: param.url,
  });
}

// 弹出提示框
export async function mobileToast(content: string) {
  invokeAsync('toast', {
    content,
  });
  return wrapResult(resultCode.ok, resultMessage.ok);
}
